// ADR 25.9.26h §3 — the one install-steps component, presentational only.
//
// Replaces 23.9.26u's `IosInstallSteps` and 25.9.26g's `MacInstallSteps`, which
// differed only in their keys; the keys now live in `INSTALL_STEPS`. Owns no
// state and decides nothing, so any surface can mount it: the account menu's
// `InstallEntry`, the install strip (the reminder row's install-first step,
// iOS-only, mounts `InstallGuide` since 16.10.26f §5).
// The `ConfirmDialogue` shape — a `<section role="dialog">` wrapping a `Card`
// — and its contract: not a modal, no portal, no `aria-modal`, no focus trap.
// `min-w-64` is carried here so the menu panel widens only while it shows.
//
// ADR 6.10.26a §3 — an entry's `fallback` renders after its list.
//
// ADR 16.10.26b §3 — never iOS: that platform gets `IosInstallDialog`, so
// `StepsPlatform` excludes it and the entry is read straight from the table.
//
// ADR 6.10.26b §3 — the dialog frame is `InstallCard`, exported so the
// handoff panel wears the same one: one frame, no second copy of it.
//
// ADR 16.10.26f §5 — never Mac Safari either: it gets the install guide's
// stepper, so `StepsPlatform` excludes it too.
//
// ADR 15.10.26d §3–§4 — a *These steps didn't work* control under the
// steps: the learner's channel
// for confirming or correcting a provisional row. Its mount marks the steps
// shown, so an install that follows is counted as `via: 'steps'`.
//
// Must not import the wasm facade: it is in 25.9.26f's boot graph.

import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { reportStepsFailed, type StepsShown } from '../telemetry/field/installReports'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { SectionHeading } from '../ui/SectionHeading'
import { markStepsShown } from './installPrompt'
import { INSTALL_STEPS, type StepsPlatform } from './installStepsTable'

function StepList({ steps }: { steps: readonly string[] }) {
  const { t } = useTranslation()
  return (
    <ol className="list-decimal space-y-1 ps-5 text-sm">
      {steps.map((key) => (
        <li key={key}>{t(key)}</li>
      ))}
    </ol>
  )
}

interface InstallCardProps {
  title: string
  children: ReactNode
  onClose(): void
}

/** The dialog frame every install panel shares: titled, outlined, closed by its OK. */
export function InstallCard({ title, children, onClose }: InstallCardProps) {
  const { t } = useTranslation()
  return (
    <section role="dialog" aria-label={title} className="min-w-64">
      <Card tone="outlined" className="space-y-2">
        <SectionHeading level={4}>{title}</SectionHeading>
        {children}
        <Button variant="secondary" onClick={onClose}>
          {t('ok')}
        </Button>
      </Card>
    </section>
  )
}

/** ADR 15.10.26d §3 — *These steps didn't work*, then the thanks line in its place. */
function StepsFailed({ platform, shown }: { platform: StepsPlatform; shown: StepsShown }) {
  const { t } = useTranslation()
  const [sent, setSent] = useState(false)
  useEffect(() => markStepsShown(platform), [platform])
  if (sent) return <p className="text-sm text-ink-muted">{t('pwa_install_steps_failed_thanks')}</p>
  const onClick = () => {
    setSent(true)
    void reportStepsFailed(shown)
  }
  return (
    <Button variant="ghost" onClick={onClick}>
      {t('pwa_install_steps_failed')}
    </Button>
  )
}

export function InstallSteps({ platform, onClose }: { platform: StepsPlatform; onClose(): void }) {
  const { t } = useTranslation()
  const entry = INSTALL_STEPS[platform]
  const shown: StepsShown = { steps: entry.steps, fallback: entry.fallback?.steps }
  return (
    <InstallCard title={t(entry.title)} onClose={onClose}>
      <StepList steps={entry.steps} />
      {entry.fallback && (
        <>
          <p className="text-sm text-ink-muted">{t(entry.fallback.lead)}</p>
          <StepList steps={entry.fallback.steps} />
        </>
      )}
      <StepsFailed platform={platform} shown={shown} />
    </InstallCard>
  )
}
